/**
 * lib/softphone.ts — Softphone WebRTC (SIP.js) contra el PBX Issabel/Asterisk.
 *
 * SIP.js se importa DINÁMICAMENTE solo en initSoftphone() — no infla el bundle
 * de Cobros/Clientes/Dashboard.
 */
import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'

export type SoftphoneStatus = 'idle' | 'connecting' | 'registered' | 'error'
export type CallState = 'none' | 'ringing-in' | 'ringing-out' | 'in-call' | 'held'

export interface SoftphoneSnapshot {
  status: SoftphoneStatus
  callState: CallState
  remoteTarget: string | null
  errorMessage: string | null
  muted: boolean
}

let snapshot: SoftphoneSnapshot = {
  status: 'idle',
  callState: 'none',
  remoteTarget: null,
  errorMessage: null,
  muted: false,
}

const listeners = new Set<(s: SoftphoneSnapshot) => void>()
function emit() {
  listeners.forEach((l) => l(snapshot))
}
function patch(p: Partial<SoftphoneSnapshot>) {
  snapshot = { ...snapshot, ...p }
  emit()
}

export function subscribeSoftphone(fn: (s: SoftphoneSnapshot) => void) {
  listeners.add(fn)
  fn(snapshot)
  return () => listeners.delete(fn)
}

export function getSoftphoneSnapshot() {
  return snapshot
}

export function isSoftphoneRegistered() {
  return snapshot.status === 'registered'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let user: any = null
let sipDomain: string | null = null
let initPromise: Promise<void> | null = null

function getRemoteAudioEl(): HTMLAudioElement {
  let el = document.getElementById('procredito-softphone-audio') as HTMLAudioElement | null
  if (!el) {
    el = document.createElement('audio')
    el.id = 'procredito-softphone-audio'
    el.autoplay = true
    document.body.appendChild(el)
  }
  return el
}

export function initSoftphone(userId: string): Promise<void> {
  if (initPromise) return initPromise

  initPromise = (async () => {
    patch({ status: 'connecting', errorMessage: null })
    try {
      // Lazy: SIP.js solo aquí (chunk aparte)
      const { SimpleUser } = await import('sip.js/lib/platform/web')

      const { data: bootstrapRows, error: bErr } = await supabase.rpc('cc_get_softphone_bootstrap')
      if (bErr) throw new Error(bErr.message)
      if (!bootstrapRows) throw new Error('El PBX aún no está configurado (Configuración → Call Center)')
      const cfg = Array.isArray(bootstrapRows) ? bootstrapRows[0] : bootstrapRows
      if (!cfg?.ws_uri || !cfg?.sip_domain) {
        throw new Error('El PBX aún no está configurado (Configuración → Call Center)')
      }

      const [{ data: profile }, { data: cred, error: credErr }] = await Promise.all([
        supabase.from('profiles').select('sip_extension, full_name').eq('id', userId).single(),
        supabase.from('cc_agent_credentials').select('sip_password').eq('user_id', userId).single(),
      ])

      if (!profile) throw new Error('No se pudo cargar tu perfil')
      const extension = profile.sip_extension
      if (!extension) throw new Error('No tienes una extensión asignada')
      if (credErr || !cred?.sip_password) throw new Error('No tienes credenciales SIP asignadas')
      const sipPassword = cred.sip_password as string

      sipDomain = cfg.sip_domain

      user = new SimpleUser(cfg.ws_uri, {
        aor: `sip:${extension}@${cfg.sip_domain}`,
        media: { remote: { audio: getRemoteAudioEl() } },
        reconnectionAttempts: 3,
        reconnectionDelay: 4,
        userAgentOptions: {
          authorizationUsername: extension,
          authorizationPassword: sipPassword,
          displayName: `${cfg.display_name_prefix ?? 'Procredito'} ${profile.full_name ?? extension}`.trim(),
        },
        delegate: {
          onServerConnect: () => patch({ errorMessage: null }),
          onServerDisconnect: (err?: Error) =>
            patch({ status: 'error', errorMessage: err?.message ?? 'Se perdió la conexión con el PBX' }),
          onRegistered: () => patch({ status: 'registered', errorMessage: null }),
          onUnregistered: () => patch({ status: 'idle' }),
          onCallReceived: () => patch({ callState: 'ringing-in' }),
          onCallCreated: () => patch({ callState: snapshot.callState === 'none' ? 'ringing-out' : snapshot.callState }),
          onCallAnswered: () => patch({ callState: 'in-call' }),
          onCallHangup: () => patch({ callState: 'none', remoteTarget: null, muted: false }),
          onCallHold: (held: boolean) => patch({ callState: held ? 'held' : 'in-call' }),
        },
      })

      await user.connect()
      await user.register()
    } catch (e) {
      user = null
      initPromise = null
      patch({ status: 'error', errorMessage: e instanceof Error ? e.message : 'No se pudo iniciar el softphone' })
    }
  })()

  return initPromise
}

export async function teardownSoftphone() {
  const u = user
  user = null
  initPromise = null
  sipDomain = null
  if (!u) return
  try { await u.hangup() } catch { /* no había llamada activa */ }
  try { await u.unregister() } catch { /* ya desconectado */ }
  try { await u.disconnect() } catch { /* ya desconectado */ }
  patch({ status: 'idle', callState: 'none', remoteTarget: null, muted: false, errorMessage: null })
}

export async function softphoneCall(rawNumber: string) {
  if (!user || snapshot.status !== 'registered' || !sipDomain) {
    throw new Error('Softphone no registrado')
  }
  const cleaned = rawNumber.replace(/[^\d+]/g, '')
  if (!cleaned) throw new Error('Número inválido')
  patch({ remoteTarget: cleaned })
  await user.call(`sip:${cleaned}@${sipDomain}`)
}

export async function softphoneAnswer() {
  if (!user) return
  await user.answer()
}

export async function softphoneDecline() {
  if (!user) return
  await user.decline()
}

export async function softphoneHangup() {
  if (!user) return
  await user.hangup()
}

export function softphoneToggleMute() {
  if (!user) return
  if (user.isMuted()) {
    user.unmute()
    patch({ muted: false })
  } else {
    user.mute()
    patch({ muted: true })
  }
}

/**
 * Ciclo de vida del softphone como hook — se usa desde AppShell (todas las
 * rutas del CRM) Y desde el Workspace real (/workspace, fuera de AppShell)
 * para que el registro sobreviva sin importar en cuál de los dos árboles de
 * rutas esté montado el usuario en este momento. Nunca se montan los dos a
 * la vez (son rutas hermanas excluyentes), así que no hay doble registro.
 */
export function useSoftphoneLifecycle(userId: string | null, extension: string | null) {
  useEffect(() => {
    if (!userId || !extension) return
    initSoftphone(userId)
    return () => { teardownSoftphone() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, extension])
}
